import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import {
  ALLERGEN_SAFETY_PILLAR_PATH,
  shouldShowContentClusterPromos,
} from "../lib/contentClusters";
import heroProductMarkup from "../assets/features/Hero Product Markup.png";
import uploadMenuItems from "../assets/features/Upload menu items.png";
import menuCmsAssignments from "../assets/features/Menu CMS + Assignments.png";
import autoGeneratedLessons from "../assets/features/Auto-generated lessons.png";
import allergenTagging from "../assets/features/Allergen Tagging.png";
import managerDashboard from "../assets/features/Manager Dashboard.png";
import locationRollupAnalytics from "../assets/features/Location Rollup Analytics.png";
import mobileLessonFlow from "../assets/features/Mobile Lesson Flow.png";
import { SEO_SHARE_IMAGES } from "../lib/seoImages";
import "../styles/homepage-redesign.css";

const startFreeHref = "https://app.speakyourmenu.com/signup";
const homepagePlaybookHref = "/#homepage-playbook";

const overviewCards = [
  {
    title: "Lessons that stick",
    body: "Short, repeatable practice built from your menu.",
  },
  {
    title: "Allergen & substitution training",
    body: "Teach safe modifications and guest questions with consistency.",
  },
  {
    title: "Multi-location oversight",
    body: "Standardize training and compare progress across locations.",
  },
];

const featureSections = [
  {
    id: "menu-setup",
    label: "MENU SETUP",
    headline: "Turn your menu into training content.",
    body: "Build the foundation once, then keep it current as your menu evolves across services and seasons.",
    bullets: [
      "Add dishes, ingredients, allergens, and dietary flags.",
      "Update items anytime as the menu changes.",
      "Attach photos to reinforce memory.",
    ],
    image: uploadMenuItems,
    imageAlt: "Menu setup screen with dishes, ingredients, allergens, and dietary tags",
  },
  {
    id: "lessons",
    label: "LESSONS",
    headline: "Lessons generated from your menu.",
    body: "Turn menu knowledge into structured practice without rebuilding content from scratch for every hire or rollout.",
    bullets: [
      "Training units built from your real items.",
      "Short daily practice for retention.",
      "Consistent standards across every employee.",
    ],
    image: autoGeneratedLessons,
    imageAlt: "Auto-generated restaurant training lessons built from a live menu",
  },
  {
    id: "safety",
    label: "SAFETY",
    headline: "Allergen and substitution confidence at the table.",
    body: "Make dietary questions easier to answer with structured menu details and repeatable training around safe modifications.",
    bullets: [
      "Teach staff what’s in each dish, clearly.",
      "Train safe substitutions and modification rules.",
      "Prepare staff to answer guest questions fast and accurately.",
    ],
    note: "Support consistent answers across shifts and locations.",
    resourceTo: ALLERGEN_SAFETY_PILLAR_PATH,
    resourceCta: "Read the allergen-safety guide",
    image: allergenTagging,
    imageAlt: "Dish detail screen with allergen tags, dietary flags, and safe modification guidance",
    priority: true,
  },
  {
    id: "tracking",
    label: "TRACKING",
    headline: "Progress visibility for managers.",
    body: "See how knowledge is landing without chasing paper checklists or relying on guesswork during service.",
    bullets: [
      "See completion and weak spots by employee.",
      "Spot where knowledge drops before it hits the floor.",
      "Keep training consistent without extra manager workload.",
    ],
    image: managerDashboard,
    imageAlt: "Manager dashboard showing staff training progress and weak knowledge areas",
  },
  {
    id: "multi-location",
    label: "MULTI-LOCATION",
    headline: "Multi-location oversight, one standard.",
    body: "Give leadership one place to review readiness, compare locations, and keep menu knowledge aligned across the group.",
    bullets: [
      "Track training across locations in one view.",
      "Compare performance and readiness by restaurant.",
      "Maintain consistent menu knowledge across the group.",
    ],
    builtFor: "Built for Directors of Ops and multi-unit operators.",
    image: locationRollupAnalytics,
    imageAlt: "Multi-location dashboard comparing training readiness across restaurant locations",
    priority: true,
  },
  {
    id: "employees",
    label: "EMPLOYEES",
    headline: "Simple for staff.",
    body: "Keep training lightweight enough for pre-shift, downtime, and day-to-day reinforcement on the floor.",
    bullets: [
      "Short sessions that fit pre-shift or downtime.",
      "Clear progress and streak-style motivation.",
      "Learn menu language, allergens, and pairings with repetition.",
    ],
    image: mobileLessonFlow,
    imageAlt: "Mobile employee lesson flow for short daily menu training practice",
  },
];

function SectionLabel({ children }) {
  return <span className="homepage-label-chip">{children}</span>;
}

function OverviewCard({ title, body }) {
  return (
    <article className="features-overview-card">
      <div className="features-overview-accent" aria-hidden="true" />
      <h2 className="homepage-card-title">{title}</h2>
      <p className="homepage-card-copy">{body}</p>
    </article>
  );
}

function FeatureSection({ section, reverse = false }) {
  const showContentClusterPromos = shouldShowContentClusterPromos();

  return (
    <section
      id={section.id}
      className={`homepage-section features-detail-section${section.priority ? " features-detail-section-priority" : ""}`}
    >
      <div className="homepage-shell">
        <div className={`features-detail-grid${reverse ? " features-detail-grid-reverse" : ""}`}>
          <div className="features-detail-copy">
            <div className="features-label-row">
              <SectionLabel>{section.label}</SectionLabel>
              {section.priority ? <span className="features-priority-chip">Priority</span> : null}
            </div>
            <h2 className="homepage-section-title features-detail-title">{section.headline}</h2>
            <p className="homepage-section-body features-detail-body">{section.body}</p>
            <ul className="features-detail-list" aria-label={`${section.headline} capabilities`}>
              {section.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
            {section.note ? <p className="features-inline-note">{section.note}</p> : null}
            {showContentClusterPromos && section.resourceTo ? (
              <Link to={section.resourceTo} className="resource-secondary-link features-resource-link">
                {section.resourceCta}
              </Link>
            ) : null}
            {section.builtFor ? <p className="features-built-for">{section.builtFor}</p> : null}
          </div>
          <div className="features-shot-shell">
            <div className="features-shot-frame">
              <img src={section.image} alt={section.imageAlt} className="features-shot-image" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function FeaturesPage() {
  const canonicalUrl = "https://speakyourmenu.com/features";
  const shareImage = SEO_SHARE_IMAGES.features;
  const description =
    "See how Speak Your Menu turns your real menu into daily training with lesson generation, allergen and substitution coaching, manager visibility, and multi-location oversight.";

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
      { "@type": "ListItem", position: 2, name: "Features", item: canonicalUrl },
    ],
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Speak Your Menu",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    description,
    url: canonicalUrl,
    image: shareImage,
    brand: { "@type": "Brand", name: "Speak Your Menu" },
    featureList: featureSections.map((section) => section.headline),
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: startFreeHref,
    },
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Features | Speak Your Menu</title>
        <meta name="description" content={description} />
        <meta property="og:title" content="Features | Speak Your Menu" />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:site_name" content="Speak Your Menu" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Features | Speak Your Menu" />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={shareImage} />
        <meta name="twitter:site" content="@speakyourmenu" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>

      <Nav />

      <main id="main-content" tabIndex="-1" className="features-page">
        <section className="homepage-section homepage-hero-section features-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="features-hero-orb features-hero-orb-left" aria-hidden="true" />
          <div className="features-hero-orb features-hero-orb-right" aria-hidden="true" />
          <div className="homepage-shell homepage-hero-grid features-hero-grid">
            <div className="homepage-hero-copy">
              <h1 className="homepage-hero-title features-hero-title">Everything you need to turn your menu into daily training.</h1>
              <p className="homepage-hero-subhead features-hero-subhead">
                Structured lessons generated from your actual menu, with progress tracking for managers and multi-location oversight.
              </p>
              <div className="features-hero-actions">
                <a
                  href={startFreeHref}
                  className="homepage-primary-cta"
                  data-cta-name="start_free"
                  data-cta-location="features_hero"
                >
                  Start free
                </a>
              </div>
              <Link to={homepagePlaybookHref} className="features-hero-secondary-link">
                <span>Prefer to read first? Get the free Menu Training Playbook.</span>
                <span className="features-hero-secondary-link-icon" aria-hidden="true">
                  -&gt;
                </span>
              </Link>
            </div>

            <div className="features-shot-shell features-hero-shot-shell">
              <div className="features-shot-frame features-shot-frame-hero">
                <img
                  src={heroProductMarkup}
                  alt="Speak Your Menu dashboard and lesson preview for restaurant menu training"
                  className="features-shot-image"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        <div className="features-sticky-nav-intro-wrap">
          <div className="homepage-shell">
            <div className="features-sticky-nav-shell">
              <div className="features-sticky-nav-intro">
                <span className="features-sticky-nav-kicker">Jump to what matters</span>
                <p className="features-sticky-nav-copy">
                  Choose the feature area you care about and jump straight to the details buyers usually compare first.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="features-sticky-nav-wrap" aria-label="Features sections">
          <div className="homepage-shell">
            <div className="features-sticky-nav-pin">
              <nav className="features-sticky-nav" aria-label="Jump to feature details">
                <span className="features-sticky-nav-label">Jump to</span>
                <a href="#menu-setup">Menu Setup</a>
                <a href="#lessons">Lessons</a>
                <a href="#safety">Safety</a>
                <a href="#tracking">Tracking</a>
                <a href="#multi-location">Multi-Location</a>
                <a href="#employees">Employees</a>
              </nav>
            </div>
          </div>
        </div>

        <section className="homepage-section features-overview-section features-rhythm-band">
          <div className="homepage-shell">
            <div className="features-section-header">
              <SectionLabel>AT A GLANCE</SectionLabel>
              <h2 className="homepage-section-title features-section-title">The core capabilities buyers ask about first.</h2>
            </div>
            <div className="features-overview-grid">
              {overviewCards.map((card) => (
                <OverviewCard key={card.title} title={card.title} body={card.body} />
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section features-proof-section features-rhythm-clean">
          <div className="homepage-shell">
            <div className="features-proof-grid">
              <div className="features-proof-copy">
                <SectionLabel>PRODUCT VIEW</SectionLabel>
                <h2 className="homepage-section-title features-section-title">Specific, visible workflows instead of generic training claims.</h2>
                <p className="homepage-section-body features-proof-body">
                  Every section below ties to a real interface already in the product, from menu setup and lesson generation through manager tracking and multi-location review.
                </p>
              </div>
              <div className="features-shot-shell">
                <div className="features-shot-frame features-shot-frame-compact">
                  <img
                    src={menuCmsAssignments}
                    alt="Menu CMS and assignments workflow"
                    className="features-shot-image"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {featureSections.map((section, index) => (
          <FeatureSection key={section.id} section={section} reverse={index % 2 === 1} />
        ))}

        <section className="homepage-section features-next-steps-section">
          <div className="homepage-shell">
            <div className="features-next-steps-block">
              <h2 className="homepage-section-title features-next-steps-title">Want to see it with your menu?</h2>
              <div className="features-next-steps-actions">
                <a
                  href={startFreeHref}
                  className="homepage-primary-cta features-next-steps-cta"
                  data-cta-name="start_free"
                  data-cta-location="features_next_steps"
                >
                  Start free
                </a>
              </div>
              <Link to={homepagePlaybookHref} className="features-hero-secondary-link features-next-steps-link">
                <span>Prefer to read first? Get the free Menu Training Playbook.</span>
                <span className="features-hero-secondary-link-icon" aria-hidden="true">
                  -&gt;
                </span>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}